import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { LogoMark, BrandText } from "./Logo";
import { NAV_LINKS } from "../config/siteConfig";
import { EASE } from "../lib/motion";

const BrandWord = ({ dark }) => (
  <Link to="/" data-testid="logo-link" className="flex items-center gap-2.5 group select-none" aria-label="Prospera Staffing home">
    <LogoMark size={44} className="transition-transform duration-500 group-hover:rotate-[-6deg]" />
    <BrandText dark={dark} />
  </Link>
);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const onHome = pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const solid = scrolled || !onHome;

  return (
    <>
      <motion.header
        initial={{ y: -80 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8, ease: EASE }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          solid ? "bg-[#F7F7F5]/90 backdrop-blur-xl border-b border-black/5 py-3" : "bg-transparent py-5"
        }`}
        data-testid="navbar"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between">
          <div>
            <BrandWord dark={!solid} />
          </div>

          <nav className="hidden lg:flex items-center gap-7">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `font-body text-sm transition-colors duration-300 relative group ${
                    solid ? "text-[#1F1F1F]" : "text-white"
                  } ${isActive ? "text-[#C62828]" : "hover:text-[#C62828]"}`
                }
              >
                {({ isActive }) => (
                  <>
                    {l.label}
                    <span className={`absolute -bottom-1 left-0 h-px bg-[#C62828] transition-all duration-300 ${isActive ? "w-full" : "w-0 group-hover:w-full"}`} />
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/contact"
              data-testid="nav-find-talent"
              className="hidden sm:inline-flex items-center gap-2 bg-[#C62828] text-white font-accent uppercase tracking-widest text-xs px-6 py-3 rounded-sm hover:bg-[#111111] transition-colors duration-400"
            >
              Find Talent <ArrowRight size={15} />
            </Link>
            <button
              onClick={() => setOpen(true)}
              data-testid="mobile-menu-open"
              className={`lg:hidden p-2 ${solid ? "text-[#1F1F1F]" : "text-white"}`}
              aria-label="Open menu"
            >
              <Menu size={26} />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.5, ease: EASE }}
            className="fixed inset-0 z-[60] bg-[#111111] text-[#F7F7F5] flex flex-col p-8"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between mb-14">
              <BrandWord dark />
              <button onClick={() => setOpen(false)} data-testid="mobile-menu-close" aria-label="Close menu">
                <X size={28} />
              </button>
            </div>
            <nav className="flex flex-col gap-1">
              {NAV_LINKS.map((l, i) => (
                <motion.div key={l.to} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 + i * 0.05 }}>
                  <NavLink
                    to={l.to}
                    end={l.to === "/"}
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={({ isActive }) => `block font-display text-3xl py-2 border-b border-white/10 transition-colors ${isActive ? "text-[#E03535]" : "hover:text-[#E03535]"}`}
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <Link
              to="/contact"
              data-testid="mobile-find-talent"
              className="mt-auto inline-flex items-center justify-center gap-2 bg-[#C62828] text-white font-accent uppercase tracking-widest text-sm px-6 py-4 rounded-sm"
            >
              Find Talent <ArrowRight size={16} />
            </Link>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
